 import React, { useState, useEffect } from "react";
import {
  Button,
  Box,
  Typography,
  Card,
  Container,
  Radio,
  RadioGroup,
  FormControlLabel,
  FormControl,
} from "@mui/material";
import { createTheme, ThemeProvider } from "@mui/material/styles";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import "../../Styles/Login.css";
import Logo from "../../Images/pari_logo_6.png";
import rightSidePic from "../../Images/right_side_pic.png";
import {  SwitchRole } from "../../Redux/Actions/authAction";
import { useUser } from "../../Context/UserContext";
import { usePermissions } from "../../Context/PermissionContext";

const theme = createTheme({
  palette: {
    primary: {
      main: '#1976d2',
    },
  },
  typography: {
    fontFamily: '"Roboto", "Helvetica", "Arial", sans-serif',
    h4: {
      fontWeight: 600,
    },
    subtitle1: {
      color: '#666',
    }
  },
  components: {
    MuiTextField: {
      styleOverrides: {
        root: {
          backgroundColor: '#f8fafc',
          borderRadius: 8,
          '& .MuiOutlinedInput-root': {
            '& fieldset': {
              borderColor: '#e2e8f0',
            },
            '&:hover fieldset': {
              borderColor: '#cbd5e1',
            },
          },
        },
      },
    },
    MuiButton: {
      styleOverrides: {
        root: {
          borderRadius: 8,
          textTransform: 'none',
          padding: '10px 0',
        },
      },
    },
    MuiCard: {
      styleOverrides: {
        root: {
          maxWidth: '750px',
          width: '100%',
          minHeight: '600px',
          padding: '2.5rem',
          margin: '2rem auto',
          '@media (max-width: 600px)': {
            padding: '1.5rem',
            margin: '1rem',
            minHeight: 'auto',
          },
        },
      },
    },
  },
});

export default function SelectAccountLayout() {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { updateUserInfo } = useUser();
  const { updatePermissions } = usePermissions();
  
  const [selectedAccount, setSelectedAccount] = useState('');
  const [accounts, setAccounts] = useState([]);
  const [loading, setLoading] = useState(false);

  // Get user roles from Redux state
  const authState = useSelector((state) => state.user);
  const userInfo = authState?.userInfo || {};
  

  // Process user roles to extract accounts from userInfo
  const processUserRoles = (userInfoData) => {
    const acceptedSubManagerAccounts = [];

    if (userInfoData.userRoles && userInfoData.userRoles.length > 0) {
      userInfoData.userRoles.forEach(role => {
        if (role.role === 'SUB_MANAGER' && role.parents && role.parents.length > 0) {
          role.parents.forEach(parent => {
            if (parent.status === 'ACCEPTED') {
              // Get group name from allowed permissions
              const groupName = parent.allowedPermissions?.[0]?.permissionGroup?.groupName || 'Unknown Group';
              
              // Accepted sub-manager account
              acceptedSubManagerAccounts.push({
                id: `${role.id}-${parent.id}`,
                userRoleId: role.id,
                parentUserRoleId: parent.parentUserRole?.id,
                accountingFirmAssignmentId: parent.accountingFirmAssignmentId || null,
                name: role.user?.name || role.user?.email?.split('@')[0] || 'Unknown User',
                email: role.user?.email || 'unknown@email.com',
                role: 'SUB_MANAGER',
                type: 'Sub-Manager Account',
                parentRole: parent.parentUserRole?.role || 'Unknown Role',
                parentEntityName: parent.parentUserRole?.entityInfo?.name || 'Unknown Entity',
                parentEmail: parent.parentUserRole?.user?.email || 'unknown@email.com',
                entityInfo: role.entityInfo,
                parentInfo: parent.parentUserRole,
                allowedPermissions: parent.allowedPermissions,
                groupName: groupName
              });
            }
          });
        }
      });
    }

    // Set the accounts data
    setAccounts(acceptedSubManagerAccounts);
    
    // Auto-select the first account if available
    if (acceptedSubManagerAccounts.length === 1) {
      setSelectedAccount(acceptedSubManagerAccounts[0].id);
    }
  };

  // Process userInfo when component mounts or userInfo changes
  useEffect(() => {
    if (userInfo && userInfo.userRoles) {
      processUserRoles(userInfo);
    }
  }, [userInfo]);

  const handleContinue = async () => {
    if (selectedAccount) {
      setLoading(true);
      try {
        // Find the selected account
        const selectedAccountData = accounts.find(acc => acc.id === selectedAccount);
        
        if (selectedAccountData) {
          // Prepare switch role request body
          const switchRequestBody = {
            userRoleId: selectedAccountData.userRoleId,
            parentUserRoleId: selectedAccountData.parentUserRoleId,
            accountingFirmAssignmentId: selectedAccountData.accountingFirmAssignmentId,
          };

          // Call switch role API using Redux action
          const switchResponse = await dispatch(SwitchRole(switchRequestBody));

          if (switchResponse.payload?.statusCode === 200 || switchResponse.payload?.statusCode === 201) {
            // Update tokens in localStorage with new tokens from response
            if (switchResponse.payload?.data?.accessToken) {
              localStorage.setItem('accessToken', switchResponse.payload.data.accessToken);
            }
            if (switchResponse.payload?.data?.refreshToken) {
              localStorage.setItem('refreshToken', switchResponse.payload.data.refreshToken);
            }

            // Update Redux store with new tokens (similar to login)
            dispatch({
              type: "user/setAuthDetails",
              payload: { 
                token: switchResponse.payload.data.accessToken, 
                refreshToken: switchResponse.payload.data.refreshToken 
              },
            });

            // Store account information
            localStorage.setItem("type", selectedAccountData.role);
            
            // If it's a SUB_MANAGER account, store permissions and parent info
            if (selectedAccountData.role === 'SUB_MANAGER' && selectedAccountData.allowedPermissions) {
              const permissions = selectedAccountData.allowedPermissions[0]?.permissionGroup?.apiNames || [];
              updatePermissions(permissions, selectedAccountData.role);
              
              // Store parent's entity info for SUB_MANAGER
              if (selectedAccountData.parentInfo?.entityInfo) {
                localStorage.setItem("parentEntityName", selectedAccountData.parentInfo.entityInfo.name);
                localStorage.setItem("parentEntityType", selectedAccountData.parentInfo.entityInfo.entityType);
              }
            }

            // Update user info in context with new tokens
            const loginResponse = {
              data: {
                accessToken: switchResponse.payload?.data?.accessToken || userInfo.accessToken,
                refreshToken: switchResponse.payload?.data?.refreshToken || userInfo.refreshToken,
                userRoles: userInfo.userRoles || []
              }
            };
            updateUserInfo(loginResponse);
            
            navigate("/dashboard");
          } else {
            console.error("Failed to switch role:", switchResponse);
          }
        }
      
      } catch (error) {
        console.error("Error switching account:", error);
      } finally {
        setLoading(false);
      }
    }
  };

  return (
    <ThemeProvider theme={theme}>
      <Box className="login-container" sx={{width: '100%'}}>
        {/* Left side - Account Selection form */}
        <Box className="login-form-container">
          <Container 
            maxWidth={false}
            className="login-wrapper"
          >
            <Card className="login-card">
              <Box className="login-card-content">
                <Box>
                  <Typography variant="h5" component="div" className="logo-wrapper">
                    <Box component="span" className="logo-icon">
                      <img 
                        src={Logo} 
                        alt="Company Logo" 
                        style={{ 
                          height: '32px', 
                          width: '100%',
                          objectFit: 'contain'
                        }} 
                      />
                    </Box>
                    <Box 
                      component="span" 
                      sx={{ 
                        backgroundColor: '#e3f2fd',
                        color: '#1976d2',
                        padding: '4px 12px',
                        borderRadius: '16px',
                        fontSize: '0.875rem',
                        fontWeight: 500,
                        border: '1px solid #1976d2'
                      }}
                    >
                      Manager Panel
                    </Box>
                  </Typography>
                </Box>

                <Box sx={{display: 'flex', flexDirection: 'column', gap: '16px'}}>
                  <Box>
                    <Typography 
                      variant="h4" 
                      className="welcome-text"
                      sx={{ mb: 1, fontSize: '2rem', fontWeight: 700, color: '#333' }}
                    >
                      Choose Account
                    </Typography>
                    
                    <Typography 
                      variant="subtitle1" 
                      className="welcome-subtitle"
                      sx={{ mb: 4, color: '#666', fontSize: '1.1rem' }}
                    >
                      Which account would you like to access?
                    </Typography>
                  </Box>

                  {/* Account Selection */}
                  {accounts.length > 0 && (
                    <>
                      <Typography variant="h6" sx={{ mb: 2, fontWeight: 600, color: '#333' }}>
                        Your Supervisor Accounts
                      </Typography>
                      <FormControl component="fieldset" sx={{ width: '100%', mb: 2}}>
                        <RadioGroup value={selectedAccount} onChange={(e) => setSelectedAccount(e.target.value)}>
                          {accounts.map((account) => (
                            <Box 
                              key={account.id}
                              sx={{ 
                                p: 3, 
                                border: selectedAccount === account.id ? '2px solid #1976d2' : '1px solid #e0e0e0',
                                borderRadius: '8px',
                                mb: 2,
                                backgroundColor: selectedAccount === account.id ? '#f3f8ff' : 'white',
                                '&:hover': {
                                  backgroundColor: selectedAccount === account.id ? '#f3f8ff' : '#f5f5f5',
                                  borderColor: selectedAccount === account.id ? '#1976d2' : '#ccc'
                                },
                                transition: 'all 0.2s ease'
                              }}
                            >
                              <FormControlLabel
                                value={account.id}
                                control={<Radio />}
                                label={
                                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', width: '100%' }}>
                                    <Box>
                                      <Typography variant="h6" sx={{ fontWeight: 600, fontSize: '1.1rem', color: '#333' }}>
                                        {account.parentEntityName}
                                      </Typography>
                                      <Typography variant="body1" sx={{ color: '#666', fontSize: '0.95rem' }}>
                                        {account.parentEmail}
                                      </Typography>
                                    </Box>
                                    <Typography variant="body1" sx={{ color: '#666', fontSize: '0.9rem', fontWeight: 500, display: 'flex', alignItems: 'flex-end', justifyContent: 'flex-end', marginLeft: '100px'
                                     }}>
                                      {account.groupName}
                                    </Typography>
                                  </Box>
                                }
                                sx={{ width: '100%', margin: 0 }}
                              />
                            </Box>
                          ))}
                        </RadioGroup>
                      </FormControl>
                    </>
                  )}

                  {/* No accounts message */}
                  {accounts.length === 0 && (
                    <Box sx={{ textAlign: 'center', py: 4 }}>
                      <Typography variant="body1" sx={{ color: '#666' }}>
                        No accounts available
                      </Typography>
                    </Box>
                  )}

                  {/* Continue Button */}
                  <Button
                    variant="contained"
                    onClick={handleContinue}
                    disabled={!selectedAccount || loading}
                    sx={{
                      bgcolor: !selectedAccount ? '#ccc' : '#1976d2',
                      color: 'white',
                      py: 1.5,
                      px: 4,
                      fontSize: '1rem',
                      width: '100%',
                      fontWeight: 500,
                      borderRadius: '8px',
                      '&:hover': {
                        bgcolor: !selectedAccount ? '#ccc' : '#1565c0'
                      },
                      '&:disabled': {
                        bgcolor: '#ccc',
                        color: '#999'
                      }
                    }}
                  >
                    {loading ? 'Processing...' : 'Continue'}
                  </Button>
                </Box>
              </Box>
            </Card>
          </Container>
        </Box>

        {/* Right side - Background Image */}
        <Box className="login-background">
          <img 
            src={rightSidePic} 
            alt="Login Background" 
            className="login-background-image"
          />
        </Box>
      </Box>
    </ThemeProvider>
  );
}
